import createDOMPurify from 'dompurify';

const DOMPurify = createDOMPurify(window);

export const ALLOWED_RICH_TAGS = [
  'p', 'br', 'ul', 'ol', 'li', 'strong', 'b', 'em', 'i', 'u', 'span',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
  'del', 's', 'mark', 'sub', 'sup', 'a',
];

export const ALLOWED_RICH_ATTR = ['href', 'target', 'rel', 'class'];

/**
 * Checks if a plain text string contains Markdown formatting.
 */
export const isMarkdown = (text) => {
  if (!text || typeof text !== 'string') return false;

  const trimmed = text.trim();
  if (!trimmed) return false;

  const patterns = [
    /^\s*[*+-]\s+[^\s]/m,                        // bullet lists: - item, * item, + item
    /^\s*\d+\.\s+[^\s]/m,                        // numbered lists: 1. item
    /\*\*[^* \n][^*]*\*\*/,                      // bold: **text**
    /__[^_ \n][^_]*__/,                          // bold: __text__
    /(?:^|[^*])\*[^* \n][^*]*\*(?:[^*]|$)/,      // italic: *text*
    /(?:^|[^_])_[^_ \n][^_]*_(?:[^_]|$)/,        // italic: _text_
    /~~[^~ \n][^~]*~~/,                          // strikethrough: ~~text~~
    /^\s*#{1,6}\s+[^\s]/m,                       // headers: # Header
    /```[\s\S]*?```/,                            // code blocks: ```code```
    /`[^`\n]+`/,                                 // inline code: `code`
    /^\s*>\s+[^\s]/m,                            // blockquotes: > quote
  ];

  return patterns.some(regex => regex.test(trimmed));
};

/**
 * Converts a Markdown string into clean, semantic HTML.
 */
export const markdownToHtml = (text) => {
  if (!text) return '';

  let html = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // 1. Code blocks (```lang ... ```)
  html = html.replace(/```([a-zA-Z0-9_-]*)\n([\s\S]*?)```/g, (_, lang, code) => {
    const escaped = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<pre><code class="${lang ? `language-${lang}` : ''}">${escaped}</code></pre>`;
  });

  // 2. Inline code (`code`)
  html = html.replace(/`([^`\n]+)`/g, (_, code) => {
    const escaped = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<code>${escaped}</code>`;
  });

  // 3. Headers (# Header to ###### Header)
  html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
  html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
  html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

  // 4. Blockquotes (> Quote)
  html = html.replace(/^\s*>\s+(.+)$/gm, '<blockquote>$1</blockquote>');
  // Merge adjacent blockquotes
  html = html.replace(/<\/blockquote>\n<blockquote>/g, '<br>');

  // 5. Lists (unordered: -, *, +)
  html = html.replace(/(?:^|\n)((?:\s*[*+-]\s+.+(?:\n|$))+)/g, (_, listBlock) => {
    const items = listBlock
      .trim()
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        const content = line.replace(/^\s*[*+-]\s+/, '').trim();
        return `<li>${content}</li>`;
      })
      .join('');
    return `\n<ul>${items}</ul>\n`;
  });

  // 6. Lists (ordered: 1., 2.)
  html = html.replace(/(?:^|\n)((?:\s*\d+\.\s+.+(?:\n|$))+)/g, (_, listBlock) => {
    const items = listBlock
      .trim()
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        const content = line.replace(/^\s*\d+\.\s+/, '').trim();
        return `<li>${content}</li>`;
      })
      .join('');
    return `\n<ol>${items}</ol>\n`;
  });

  // 7. Text Styles
  // Bold (**text** or __text__)
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Strikethrough (~~text~~)
  html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>');

  // Italic (*text* or _text_)
  html = html.replace(/(?:^|[^*])\*([^* \n][^*]*)\*(?:[^*]|$)/g, (match, p1) => {
    const prefix = match.startsWith('*') ? '' : match[0];
    const suffix = match.endsWith('*') ? '' : match[match.length - 1];
    return `${prefix}<em>${p1}</em>${suffix}`;
  });
  html = html.replace(/(?:^|[^_])_([^_ \n][^_]*)_(?:[^_]|$)/g, (match, p1) => {
    const prefix = match.startsWith('_') ? '' : match[0];
    const suffix = match.endsWith('_') ? '' : match[match.length - 1];
    return `${prefix}<em>${p1}</em>${suffix}`;
  });

  // 8. Paragraphs and Line Breaks
  const blocks = html.split(/\n\s*\n/);
  const formattedBlocks = blocks.map(block => {
    const trimmed = block.trim();
    if (!trimmed) return '';
    if (/^<(h[1-6]|ul|ol|pre|blockquote)/i.test(trimmed)) {
      return trimmed;
    }
    const withBr = trimmed.replace(/\n/g, '<br>');
    return `<p>${withBr}</p>`;
  });

  const finalHtml = formattedBlocks.filter(Boolean).join('');
  return DOMPurify.sanitize(finalHtml, {
    ALLOWED_TAGS: ALLOWED_RICH_TAGS,
    ALLOWED_ATTR: ALLOWED_RICH_ATTR,
  });
};

/**
 * Normalizes HTML copied from ChatGPT, Gemini, Microsoft Word, Google Docs, etc.
 * Resolves nested block issues, font weight mismatches, and pseudo-lists.
 */
export const cleanPastedHtml = (rawHtml) => {
  if (!rawHtml || typeof rawHtml !== 'string') return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');
  const body = doc.body;

  // 1. Remove Word junk, scripts, styles, XML islands, and comments
  const elementsToRemove = body.querySelectorAll('script, style, xml, meta, link, o\\:p');
  elementsToRemove.forEach(el => el.remove());

  // 2. Normalize List Items (Unwrap <p>, <div>, <section> inside <li>)
  body.querySelectorAll('li').forEach(li => {
    const innerBlocks = Array.from(li.children).filter(child =>
      ['P', 'DIV', 'SECTION', 'ARTICLE', 'BLOCKQUOTE'].includes(child.tagName)
    );

    if (innerBlocks.length > 0) {
      innerBlocks.forEach(block => {
        while (block.firstChild) {
          li.insertBefore(block.firstChild, block);
        }
        block.remove();
      });
    }
  });

  // 3. Convert Pseudo-Lists (paragraphs/divs starting with bullet characters or numbers)
  const bulletRegex = /^[\s\u00A0]*[•·●■◆\u2022\u2023\u25E6\u2043\u2219\-*]\s+/;
  const numberRegex = /^[\s\u00A0]*\d+[\.\)]\s+/;

  const topLevelChildren = Array.from(body.children);
  let currentList = null;
  let currentListType = null; // 'ul' | 'ol'

  topLevelChildren.forEach(child => {
    const text = child.textContent || '';
    const isPOrDiv = ['P', 'DIV'].includes(child.tagName);

    if (isPOrDiv && bulletRegex.test(text)) {
      if (!currentList || currentListType !== 'ul') {
        currentList = doc.createElement('ul');
        currentListType = 'ul';
        child.parentNode.insertBefore(currentList, child);
      }
      const li = doc.createElement('li');
      li.innerHTML = child.innerHTML.replace(bulletRegex, '');
      currentList.appendChild(li);
      child.remove();
    } else if (isPOrDiv && numberRegex.test(text)) {
      if (!currentList || currentListType !== 'ol') {
        currentList = doc.createElement('ol');
        currentListType = 'ol';
        child.parentNode.insertBefore(currentList, child);
      }
      const li = doc.createElement('li');
      li.innerHTML = child.innerHTML.replace(numberRegex, '');
      currentList.appendChild(li);
      child.remove();
    } else {
      currentList = null;
      currentListType = null;
    }
  });

  // 4. Normalize Bold Styling (ChatGPT / Gemini use font-weight: 600 or Tailwind classes)
  body.querySelectorAll('*').forEach(el => {
    const style = el.getAttribute('style') || '';
    const className = el.className || '';
    const inlineWeight = el.style?.fontWeight || '';

    const hasBoldStyle =
      ['600', '700', '800', '900', 'bold', 'bolder'].includes(inlineWeight) ||
      /font-weight\s*:\s*(600|700|800|900|bold|bolder)/i.test(style);

    const hasBoldClass =
      typeof className === 'string' &&
      /\b(font-(semibold|bold|extrabold|black)|fw-bold|fw-semibold|bold)\b/i.test(className);

    if (hasBoldStyle || hasBoldClass) {
      if (el.tagName !== 'STRONG' && el.tagName !== 'B') {
        const strong = doc.createElement('strong');
        while (el.firstChild) {
          strong.appendChild(el.firstChild);
        }
        el.appendChild(strong);
      }
    }
  });

  // 5. Normalize Italic Styling
  body.querySelectorAll('*').forEach(el => {
    const style = el.getAttribute('style') || '';
    const className = el.className || '';
    const inlineStyle = el.style?.fontStyle || '';

    const hasItalicStyle =
      inlineStyle === 'italic' ||
      /font-style\s*:\s*italic/i.test(style);

    const hasItalicClass =
      typeof className === 'string' &&
      /\b(italic|fst-italic)\b/i.test(className);

    if (hasItalicStyle || hasItalicClass) {
      if (el.tagName !== 'EM' && el.tagName !== 'I') {
        const em = doc.createElement('em');
        while (el.firstChild) {
          em.appendChild(el.firstChild);
        }
        el.appendChild(em);
      }
    }
  });

  // 6. Normalize Underline Styling
  body.querySelectorAll('*').forEach(el => {
    const style = el.getAttribute('style') || '';
    const className = el.className || '';
    const inlineDecoration = el.style?.textDecoration || '';

    const hasUnderlineStyle =
      inlineDecoration.includes('underline') ||
      /text-decoration\s*:\s*[^;]*underline/i.test(style);

    const hasUnderlineClass =
      typeof className === 'string' &&
      /\b(underline|text-decoration-underline)\b/i.test(className);

    if (hasUnderlineStyle || hasUnderlineClass) {
      if (el.tagName !== 'U') {
        const u = doc.createElement('u');
        while (el.firstChild) {
          u.appendChild(el.firstChild);
        }
        el.appendChild(u);
      }
    }
  });

  // 7. Normalize Strikethrough Styling
  body.querySelectorAll('*').forEach(el => {
    const style = el.getAttribute('style') || '';
    const inlineDecoration = el.style?.textDecoration || '';

    const hasLineThrough =
      inlineDecoration.includes('line-through') ||
      /text-decoration\s*:\s*[^;]*line-through/i.test(style) ||
      ['S', 'STRIKE'].includes(el.tagName);

    if (hasLineThrough && el.tagName !== 'DEL') {
      const del = doc.createElement('del');
      while (el.firstChild) {
        del.appendChild(el.firstChild);
      }
      el.appendChild(del);
    }
  });

  // 8. Normalize Code Blocks (<pre><code ...>)
  body.querySelectorAll('pre').forEach(pre => {
    pre.classList.add('ql-syntax');
  });

  // 9. Preserve empty paragraphs for line spacing
  body.querySelectorAll('p').forEach(p => {
    if (!p.childNodes.length || (p.childNodes.length === 1 && p.textContent.trim() === '')) {
      p.innerHTML = '<br>';
    }
  });

  // 10. Clean and sanitize through DOMPurify
  return DOMPurify.sanitize(body.innerHTML, {
    ALLOWED_TAGS: ALLOWED_RICH_TAGS,
    ALLOWED_ATTR: ALLOWED_RICH_ATTR,
  });
};
